import React, { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Input, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, LoadingButton,
} from '@redpanda/ui';
import { useUnsavedChangesWarning } from '@redpanda/utils';
import { createAccount, updateAccount } from '../services/api';
import { Account, AccountType, ACCOUNT_TYPES, isAccountUsed, useAccountStoreBase } from '../stores/accountStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';

interface AccountDialogProps {
  /** 有值＝編輯；沒有＝新增 */
  account?: Account;
  onClose: () => void;
}

interface FormData {
  code: string;
  name: string;
  type: AccountType | '';
  description: string;
}

/**
 * 新增／編輯會計科目（新增、編輯原本是兩個幾乎一樣的對話框）
 * 等伺服器回應才關（§3-17）：最常見的失敗是科目代號重複，要直接標在欄位下方讓使用者改
 */
const AccountDialog: React.FC<AccountDialogProps> = ({ account, onClose }) => {
  const { t } = useTranslation();
  const isEditMode = !!account;
  const typeLocked = !!account && isAccountUsed(account);
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, formState: { errors, isDirty }, reset, setValue, watch, setError } = useForm<FormData>({
    defaultValues: { code: '', name: '', type: '', description: '' },
  });

  // 初始值在繪製前帶入，編輯時不會先閃一格空白欄位（R31）
  useLayoutEffect(() => {
    reset(account
      ? { code: account.code, name: account.name, type: account.type, description: account.description || '' }
      : { code: '', name: '', type: '', description: '' });
  }, [account, reset]);

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty && !saving, onClose);

  const onSubmit = async (data: FormData) => {
    const payload = { code: data.code.trim(), name: data.name.trim(), type: data.type, description: data.description.trim() || null };
    setSaving(true);
    try {
      const response = account ? await updateAccount(account.id, payload) : await createAccount(payload);
      const store = useAccountStoreBase.getState();
      if (account) store.updateItem(account.id, response.data);
      else store.addItem(response.data);
      toast.success(t(account ? 'common.messages.updateSuccess' : 'common.messages.createSuccess'));
      notifyDataChanged('accounts');
      onClose();
    } catch (error: any) {
      const code = error?.response?.data?.code;
      if (code === 'UNIQUE_CONSTRAINT_FAILED') {
        setError('code', { type: 'manual', message: t('accounts.toast.duplicateCode') });
      } else {
        toast.error(apiErrorMessage(error, account ? 'common.messages.updateFailed' : 'common.messages.createFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogShell onClose={handleClose} closeDisabled={saving} className="max-w-lg">
        <DialogHeader
          title={isEditMode ? t('accounts.edit') : t('accounts.add')}
          onClose={handleClose}
          closeDisabled={saving}
          closeLabel={t('common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-4 gap-y-5">
              <FormFieldWithError label={t('accounts.code')} required error={errors.code?.message} htmlFor="code">
                <Input
                  id="code"
                  inputMode="numeric"
                  autoFocus={!isEditMode}
                  {...register('code', {
                    required: t('common.forms.validation.required'),
                    pattern: { value: /^[0-9]{4,6}$/, message: t('common.form.accountCodePattern') },
                  })}
                  variant={errors.code ? 'error' : 'default'}
                />
              </FormFieldWithError>
              <FormFieldWithError label={t('accounts.name')} required error={errors.name?.message} htmlFor="name">
                <Input
                  id="name"
                  {...register('name', { validate: value => !!value.trim() || t('common.forms.validation.required') })}
                  variant={errors.name ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>

            <FormFieldWithError
              label={t('accounts.type')}
              required
              error={errors.type?.message}
              helpText={typeLocked ? t('accounts.dialog.typeLocked') : undefined}
              htmlFor="type"
            >
              <input type="hidden" {...register('type', { required: t('common.forms.validation.required') })} />
              <CustomSelect
                id="type"
                value={watch('type')}
                onChange={value => setValue('type', value as AccountType, { shouldDirty: true, shouldValidate: true })}
                disabled={typeLocked}
                placeholder={t('accounts.dialog.selectType')}
                variant={errors.type ? 'error' : 'default'}
                options={ACCOUNT_TYPES.map(type => ({ value: type, label: t(`accounts.types.${type}`) }))}
              />
            </FormFieldWithError>

            <FormFieldWithError label={t('accounts.description')} htmlFor="description">
              <Input id="description" {...register('description')} />
            </FormFieldWithError>
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} disabled={saving} className={dialogButtonClass.cancel}>
              {t('common.actions.cancel')}
            </button>
            <LoadingButton type="submit" loading={saving} className={dialogButtonClass.primary}>
              {isEditMode ? t('common.actions.save') : t('common.actions.create')}
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('modal:unsavedChanges.title')}
        message={t('modal:unsavedChanges.message')}
        confirmText={t('modal:unsavedChanges.confirmButton')}
        cancelText={t('modal:unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default AccountDialog;
